/**
 * Hook to set up error handling for the current component
 * Web components can use this to inject toast-based error handling
 */

import { useEffect } from 'react';
import { Platform } from 'react-native';
import { setGlobalErrorHandler, resetGlobalErrorHandler, ErrorDisplayFunction } from '@/utils/common/errorHandling';

/**
 * Hook to configure error handling for the current component
 * On web, this can inject toast notifications
 * On mobile, it uses the default alert-based error handling
 * 
 * @param customErrorHandler - Optional custom error handler (e.g., toast on web)
 * 
 * @example
 * // Web usage with toast
 * const { showError } = useToast();
 * useErrorHandler(showError);
 * 
 * @example
 * // Mobile usage (uses default alerts)
 * useErrorHandler();
 */
export const useErrorHandler = (customErrorHandler?: ErrorDisplayFunction) => {
  useEffect(() => {
    if (customErrorHandler) {
      setGlobalErrorHandler(customErrorHandler);
    }

    // Cleanup: reset to default when component unmounts
    return () => {
      if (customErrorHandler) {
        resetGlobalErrorHandler();
      }
    };
  }, [customErrorHandler]);
};

